import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import {
  figureDelta,
  figureValue,
  formatDecimal,
  formatInteger,
  formatPercent,
  lines,
} from '@components/charts/membership/revenue/figures'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import useRevenueStatistics from '@components/charts/membership/revenue/useRevenueStatistics'
import { ResponseMembershipEngagementAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { Col, Flex, Row, Typography } from 'antd'

const { Text } = Typography

const DELTA_LABEL = 'vs last period'

/**
 * "Engagement" (Figma 1013:2244), on the Analytics page: follows the page's
 * date range and program, each figure with its delta vs the previous window of
 * the same length. Every number is the API's.
 */
const Engagement = () => {
  const { data, isLoading, error } =
    useRevenueStatistics<ResponseMembershipEngagementAnalyticsStatistics>(
      'membership-engagement',
      true
    )

  const engaged = figureValue(data?.engagedMembers)
  const active = figureValue(data?.activeMembers)
  const rateEngaged = figureValue(data?.renewalRateEngaged)
  const rateDormant = figureValue(data?.renewalRateDormant)
  const covers = figureValue(data?.coversPerMember)
  const reservations = figureValue(data?.reservationsPerMember)
  const registrationsPerMember = figureValue(data?.eventRegistrationsPerMember)
  const registrations = figureValue(data?.eventRegistrations)
  const checkIns = figureValue(data?.eventCheckIns)
  const held = figureValue(data?.eventRegistrationsHeld)

  return (
    <Flex vertical gap={24}>
      <SectionHead
        title="Engagement"
        caption="Follows the selected date range · change vs the previous period of the same length"
      />
      <Row gutter={[24, 24]} align="stretch">
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Engaged members"
            tooltip="Members active during the selected period with at least one restaurant reservation or event check-in in that period. Shorter periods give lower rates."
            value={formatPercent(figureValue(data?.engagedMembersRate), 0)}
            secondary={lines(
              engaged !== null &&
                active !== null &&
                `${formatInteger(engaged)} of ${formatInteger(active)} members active over the period`,
              rateEngaged !== null &&
                rateDormant !== null &&
                `Engaged members renew at ${formatPercent(rateEngaged, 0)} vs ${formatPercent(rateDormant, 0)} for the others`
            )}
            delta={{
              // Points: `deltaValue`, not the percent change.
              value: figureDelta(data?.engagedMembersRate, 'absolute'),
              unit: 'points',
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Restaurant reservations per member"
            tooltip="Restaurant reservations made during the selected period, per member active in that period."
            value={
              reservations !== null ? formatDecimal(reservations) : undefined
            }
            secondary={
              covers !== null ? `${formatDecimal(covers)} covers` : undefined
            }
            delta={{
              value: figureDelta(data?.reservationsPerMember),
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Event registrations per member"
            tooltip="Event registrations made during the selected period, per member active in that period."
            value={
              registrationsPerMember !== null
                ? formatDecimal(registrationsPerMember)
                : undefined
            }
            secondary={
              registrations !== null
                ? `${formatInteger(registrations)} registrations`
                : undefined
            }
            delta={{
              value: figureDelta(data?.eventRegistrationsPerMember),
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
        <Col flex="1">
          <AnalyticsStatistic
            variant="overview"
            title="Event attendance rate"
            tooltip="Share of registrations to events held during the selected period that ended with a check-in."
            value={formatPercent(figureValue(data?.eventAttendanceRate), 0)}
            secondary={
              checkIns !== null && held !== null
                ? `${formatInteger(checkIns)} check-ins of ${formatInteger(held)} registrations · events held over the period`
                : undefined
            }
            delta={{
              value: figureDelta(data?.eventAttendanceRate, 'absolute'),
              unit: 'points',
              label: DELTA_LABEL,
            }}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      {error && <Text type="danger">{error}</Text>}
    </Flex>
  )
}

export default Engagement
